import { useState } from 'react';
import useGetSkuQuery from '@/api/queries/useGetSkuQuery';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import NumberInput from '@/components/NumberInput';
import PageHeader from '@/components/PageHeader';
import DiscountStar from '@/modules/store/components/DiscountStar';
import ImageDialog from '@/modules/store/components/ImageDialog';
import ImageGallery from '@/modules/store/components/ImageGallery';
import Price from '@/modules/store/components/Price';
import { useCart } from '@/modules/store/contexts/CartContext';
import { CartItemImpl } from '@/modules/store/models/CartItem';
import { formatDimensions } from '@/modules/store/models/Dimensions';
import { Image } from '@/modules/store/models/Image';
import { formatNetContent } from '@/modules/store/models/NetContent';
import { SkuAssociation } from '@/modules/store/models/Sku';
import { CartPath } from '@/modules/store/paths';
import ProductPriceService from '@/modules/store/services/ProductPriceService';
import { ArrowLeftIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

const productPriceService = new ProductPriceService();

const ProductDetails = () => {
  const [quantity, setQuantity] = useState<number>(1);
  const [selectedImage, setSelectedImage] = useState<Image | null>(null);

  const { skuId } = useParams<{ skuId: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const { sku, isLoading, error } = useGetSkuQuery(skuId, [SkuAssociation.ALL]);

  const handleAddToCart = () => {
    if (!sku) return;

    const qty = quantity || sku.moq || 1;
    addToCart(sku.supplierId, new CartItemImpl(sku.id, sku.supplierId, qty));

    toast.custom((t) => (
      <CustomToast
        id={t.id}
        message={`Successfully added ${sku.name} to the cart!`}
        visible={t.visible}
      />
    ));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  if (error || !sku) {
    return (
      <div className="p-6">
        <Alert type="error" message="Failed to load product details." />
        <Button
          variant="secondary"
          icon={<ArrowLeftIcon className="w-5 h-5 mr-2" />}
          onClick={() => navigate(-1)}
          className="mt-4"
        >
          Back
        </Button>
      </div>
    );
  }

  const unitPrice = productPriceService.calculateUnitPrice(sku);

  return (
    <div className="space-y-6">
      <PageHeader title={sku.name}>
        <Button
          variant="secondary"
          icon={<ArrowLeftIcon className="w-5 h-5 mr-2" />}
          onClick={() => navigate(-1)}
        >
          Back to Products
        </Button>
      </PageHeader>

      <div className="bg-white shadow-sm rounded-2xl p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
        <ImageGallery images={sku.images} onClick={(image) => setSelectedImage(image)} />

        {/* PRODUCT INFO */}
        <div className="flex flex-col gap-4 justify-between">
          <div>
            <h2 className="text-2xl font-semibold mb-2">{sku.name}</h2>
            <p className="text-gray-600 mb-2">SKU: {sku.externalCode}</p>

            <table className="text-sm text-gray-700 mb-4 w-80">
              <tbody>
                {sku.moq !== undefined && sku.moq > 0 && (
                  <tr>
                    <td className="font-medium pr-4 py-1">Min. Order:</td>
                    <td>{sku.moq}</td>
                  </tr>
                )}
                {sku.quantity !== undefined && sku.quantity > 0 && (
                  <tr>
                    <td className="font-medium pr-4 py-1">Unit Count:</td>
                    <td>{sku.quantity}</td>
                  </tr>
                )}
                {sku.netContent && (
                  <tr>
                    <td className="font-medium pr-4 py-1">Net Content:</td>
                    <td>{formatNetContent(sku.netContent)}</td>
                  </tr>
                )}
                {sku.dimensions && formatDimensions(sku.dimensions) && (
                  <tr>
                    <td className="font-medium pr-4 py-1">Dimensions:</td>
                    <td>{formatDimensions(sku.dimensions)}</td>
                  </tr>
                )}
                {sku.status && (
                  <tr>
                    <td className="font-medium pr-4 py-1">Status:</td>
                    <td>
                      <span
                        className={`
                          inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium
                          ${sku.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
                        `}
                      >
                        {sku.status === 'ACTIVE' ? 'Available for Sale' : 'Not Available'}
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {unitPrice && (
              <div className="flex items-center gap-2 mb-4">
                <Price price={unitPrice} />
                <DiscountStar price={unitPrice} />
              </div>
            )}

            {sku.description && (
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{sku.description}</p>
            )}
          </div>

          {/* ADD TO CART SECTION */}
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <NumberInput className="w-full sm:w-24" value={quantity} onChange={setQuantity} />
            <Button
              onClick={handleAddToCart}
              icon={<ShoppingCartIcon className="w-5 h-5 mr-2" />}
              variant="primary"
              size="lg"
              nowrap
            >
              Add to Cart
            </Button>
          </div>

          <Button
            variant="success"
            size="sm"
            className="mt-2 self-start"
            onClick={() => navigate(CartPath)}
          >
            Go to Cart
          </Button>
        </div>
      </div>

      <ImageDialog image={selectedImage} onClose={() => setSelectedImage(null)} />
    </div>
  );
};

export default ProductDetails;
